import type { Ref } from 'vue'

export const TRUST_DAY_OPTIONS = [7, 14, 30] as const
export const TRUST_DEFAULT_DAYS = 14

const COOKIE_MAX_AGE_S = 90 * 24 * 60 * 60

interface TrustedDeviceCookie {
  token: string
  until: number
  /** Empreinte SHA-256 de l'e-mail du compte propriétaire (masque la case « Faire confiance » pour ce compte). */
  account?: string
}

interface SessionPolicyCookie {
  trusted: boolean
  until: number
}

interface ExtensionSession {
  data: { access_token: string, refresh_token: string, expires: number }
}

const extensionErrorCode = (error: any): string | undefined =>
  error?.data?.errors?.[0]?.extensions?.code

const accountFingerprint = async (email: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()))
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

/** Erreur définitive d'authentification (jeton refusé), par opposition à une panne réseau ou serveur. */
const isDefinitiveAuthError = (error: any): boolean => {
  const status = Number(error?.statusCode ?? error?.status ?? error?.response?.status)
  return status === 400 || status === 401 || status === 403
}

/**
 * Appareil de confiance et renouvellement de session (avec ou sans 2FA).
 * - Case cochée : session renouvelée automatiquement pendant la durée choisie (jusqu'à 30 jours), puis connexion complète.
 *   Si le 2FA est actif, le code OTP n'est en plus plus redemandé sur cet appareil.
 * - Case non cochée : aucun renouvellement, l'utilisateur est déconnecté à l'expiration du JWT Directus.
 * Les erreurs réseau ne déconnectent jamais l'utilisateur.
 */
export const useDeviceTrust = () => {
  const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & {
    _deviceTrustRenewal?: Promise<boolean> | null
    _deviceTrustCookies?: { device: Ref<TrustedDeviceCookie | null>, session: Ref<SessionPolicyCookie | null> }
  }
  const baseURL = useDirectusUrl()
  const { cookieSecure, cookieNameToken, cookieNameRefreshToken } = useRuntimeConfig().public.directus
  const { token, refreshToken, expires, refreshTokens } = useDirectusToken()

  const cookieOptions = { maxAge: COOKIE_MAX_AGE_S, sameSite: 'lax' as const, secure: cookieSecure }

  // Refs partagées par instance Nuxt : plusieurs useCookie() sur le même cookie ne se resynchronisent pas
  // (hors Chromium), ce qui laissait le plugin de renouvellement avec une politique de session périmée après la connexion.
  nuxtApp._deviceTrustCookies ??= {
    device: useCookie<TrustedDeviceCookie | null>('ap_trusted_device', cookieOptions),
    session: useCookie<SessionPolicyCookie | null>('ap_session', cookieOptions),
  }
  const { device, session } = nuxtApp._deviceTrustCookies

  const validDeviceToken = (): string | null =>
    device.value && device.value.until > Date.now() ? device.value.token : null

  const applySession = (response: ExtensionSession) => {
    token.value = response.data.access_token
    refreshToken.value = response.data.refresh_token
    expires.value = (response.data.expires ?? 0) + Date.now()
  }

  /**
   * Marque cet appareil comme de confiance (à appeler juste après une connexion).
   * L'extension exige une ré-authentification : OTP si le 2FA est actif, mot de passe sinon.
   */
  const registerDevice = async (days: number, email?: string, reauth: { otp?: string, password?: string } = {}) => {
    const response = await $fetch<{ data: { token: string, expires_at: string } }>('/account-security/trusted-devices/register', {
      baseURL,
      method: 'POST',
      headers: { Authorization: `Bearer ${token.value}` },
      // L'ancien jeton de cet appareil (s'il existe) est remplacé côté serveur : pas d'entrée orpheline
      body: { days, replace_token: validDeviceToken() ?? undefined, otp: reauth.otp || undefined, password: reauth.password || undefined },
    })

    device.value = {
      token: response.data.token,
      until: new Date(response.data.expires_at).getTime(),
      account: email ? await accountFingerprint(email) : undefined,
    }
  }

  /** Appareil de confiance a priori lié à ce compte (cookie antérieur sans empreinte : supposé lié, vérifié après connexion). */
  const isTrustedFor = async (email: string): Promise<boolean> => {
    if (!validDeviceToken()) {
      return false
    }
    return !device.value!.account || !email.trim() || device.value!.account === await accountFingerprint(email)
  }

  /**
   * Vérifie auprès de Directus, une fois connecté, que l'appareil de confiance appartient bien au compte courant
   * (appareil partagé entre plusieurs volontaires). Complète l'empreinte du compte dans le cookie si besoin.
   */
  const confirmDeviceOwnership = async (email?: string): Promise<boolean> => {
    const deviceToken = validDeviceToken()
    if (!deviceToken) {
      return false
    }

    try {
      const response = await $fetch<{ data: { current: boolean }[] }>('/account-security/trusted-devices', {
        baseURL,
        headers: { 'Authorization': `Bearer ${token.value}`, 'x-trusted-device': deviceToken },
      })
      const owned = response.data.some(row => row.current)
      if (owned && email && !device.value!.account) {
        device.value = { ...device.value!, account: await accountFingerprint(email) }
      }
      return owned
    }
    catch {
      return false
    }
  }

  /**
   * Connexion sans OTP depuis un appareil de confiance.
   * Retourne null si l'appareil n'est (ou n'est plus) reconnu : l'appelant demande alors le code OTP.
   */
  const loginWithTrustedDevice = async (email: string, password: string): Promise<ExtensionSession | null> => {
    const deviceToken = validDeviceToken()
    if (!deviceToken) {
      return null
    }

    try {
      return await $fetch<ExtensionSession>('/account-security/trusted-devices/login', {
        baseURL,
        method: 'POST',
        body: { email, password, device_token: deviceToken },
      })
    }
    catch (error: any) {
      if (extensionErrorCode(error) === 'INVALID_DEVICE') {
        device.value = null
        return null
      }
      throw error
    }
  }

  const startSession = (trusted: boolean) => {
    const trustedUntil = validDeviceToken() ? device.value!.until : 0
    session.value = trusted && trustedUntil
      ? { trusted: true, until: trustedUntil }
      : { trusted: false, until: 0 }
  }

  /** Déconnexion volontaire : plus de renouvellement automatique (l'appareil reste de confiance pour l'OTP). */
  const endSession = () => {
    session.value = null
  }

  const forgetDevice = () => {
    device.value = null
    session.value = null
  }

  const renewFromTrustedDevice = async (): Promise<boolean> => {
    const deviceToken = validDeviceToken()
    if (!deviceToken) {
      return false
    }

    try {
      applySession(await $fetch<ExtensionSession>('/account-security/trusted-devices/session', {
        baseURL,
        method: 'POST',
        body: { device_token: deviceToken },
      }))
      return true
    }
    catch (error: any) {
      if (extensionErrorCode(error) === 'INVALID_DEVICE') {
        forgetDevice()
        return false
      }
      throw error
    }
  }

  const renew = async (): Promise<boolean> => {
    const policy = session.value

    // Session sans appareil de confiance : pas de renouvellement, connexion classique à l'expiration du JWT
    if (policy && !policy.trusted) {
      return false
    }

    // Fin de la durée de confiance : retour à la connexion classique
    if (policy && Date.now() > policy.until) {
      return false
    }

    try {
      if (await refreshTokens()) {
        return true
      }
    }
    catch (error: any) {
      if (!isDefinitiveAuthError(error)) {
        throw error
      }
    }

    return policy?.trusted ? await renewFromTrustedDevice() : false
  }

  /**
   * Renouvelle le jeton d'accès. Retourne false uniquement quand la session est définitivement terminée ;
   * lève l'erreur d'origine en cas de panne réseau/serveur (l'appelant ne doit alors pas déconnecter).
   */
  const readSharedCookie = (name: string): string | null => {
    const match = document.cookie.split('; ').find(entry => entry.startsWith(`${name}=`))
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null
  }

  /**
   * Plusieurs onglets partagent les mêmes cookies et le refresh token Directus est à usage unique :
   * le renouvellement est sérialisé entre onglets, et un onglet qui trouve la session déjà renouvelée par un autre
   * reprend ses jetons au lieu de rejouer un refresh token périmé (déconnexion « token invalide ou expiré »).
   */
  const renewAcrossTabs = (): Promise<boolean> => {
    if (import.meta.server || !navigator.locks) {
      return renew()
    }

    const expiresBefore = expires.value ?? 0

    return navigator.locks.request('ap-session-renew', async () => {
      const sharedExpires = Number(readSharedCookie('directus_token_expired_at')) || 0
      const sharedToken = readSharedCookie(cookieNameToken)
      const sharedRefresh = readSharedCookie(cookieNameRefreshToken)

      if (sharedExpires > expiresBefore && sharedExpires > Date.now() && sharedToken && sharedRefresh) {
        token.value = sharedToken
        refreshToken.value = sharedRefresh
        expires.value = sharedExpires
        return true
      }

      return renew()
    })
  }

  const renewSession = (): Promise<boolean> => {
    // Une seule requête de renouvellement à la fois, par instance Nuxt (jamais partagée entre deux requêtes SSR)
    nuxtApp._deviceTrustRenewal ??= renewAcrossTabs().finally(() => {
      nuxtApp._deviceTrustRenewal = null
    })
    return nuxtApp._deviceTrustRenewal!
  }

  return {
    /** Session ouverte sans appareil de confiance : déconnexion à l'expiration du JWT (sessions antérieures sans politique : false). */
    isUntrustedSession: () => session.value?.trusted === false,
    hasTrustedDevice: () => Boolean(validDeviceToken()),
    isTrustedFor,
    confirmDeviceOwnership,
    registerDevice,
    loginWithTrustedDevice,
    startSession,
    endSession,
    forgetDevice,
    renewSession,
  }
}
